import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@core-ui/Components/ui/table';

import { AlertTriangle, Check, Minus, Pencil, Trash2, } from 'lucide-react';

import type { ContactInfoDto } from '@features/contacts/types/contacts.types';
import { ContactsFallbackComponent } from '@features/contacts/components/ContactsFallbackComponent.tsx';
import { ContactsTableSkeleton } from '@features/contacts';
import { IconFolder } from '@tabler/icons-react';

interface ContactsTableProps {
  isLoading: boolean
  activeTab: string,
  contacts?: ContactInfoDto[];
  skeletonRowsCount?: number;
  onEditContact?: (contact: ContactInfoDto) => void;
  onDeleteContact?: (contactId: string) => void;
}

function formatDisplayValue (value: unknown): string {
  const stringValue = String(value ?? '').trim();
  return stringValue === '' ? '—' : stringValue;
}

function getContactNotes (contact: ContactInfoDto): string {
  return formatDisplayValue(contact.notes ?? contact.notesSDR ?? contact.notesAgent ?? '');
}

interface ContactStatusIconProps {
  errorMessages?: string[];
  contactName?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function translateErrorMessage (msg: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  // Map known backend error patterns to translation keys
  const lowerMsg = msg.toLowerCase();

  if (lowerMsg.includes('invalid phone') || lowerMsg.includes('phone number format')) {
    return t('contacts.errors.invalidPhoneFormat');
  }

  if (lowerMsg.includes('missing phone') || lowerMsg.includes('phone is required')) {
    return t('contacts.errors.missingPhone');
  }

  if (lowerMsg.includes('missing name') || lowerMsg.includes('name is required')) {
    return t('contacts.errors.missingName');
  }

  if (lowerMsg.includes('invalid email') || lowerMsg.includes('email format')) {
    return t('contacts.errors.invalidEmail');
  }

  if (lowerMsg.includes('duplicate')) {
    return t('contacts.errors.duplicateContact');
  }

  // Handle "Name repeat X times" pattern
  const nameRepeatMatch = msg.match(/name\s+repeat\s+(\d+)\s+times?/i);
  if (nameRepeatMatch !== null) {
    return t('contacts.errors.nameRepeat', { count: nameRepeatMatch[1] });
  }

  // Handle "Phone repeat X times" pattern
  const phoneRepeatMatch = msg.match(/phone\s+repeat\s+(\d+)\s+times?/i);
  if (phoneRepeatMatch !== null) {
    return t('contacts.errors.phoneRepeat', { count: phoneRepeatMatch[1] });
  }

  // Return original message if no translation found
  return msg;
}

function ContactStatusIcon ({ errorMessages, contactName, t }: ContactStatusIconProps): React.JSX.Element {
  const warningsCount = errorMessages?.length ?? 0;

  const handleShowWarnings = (): void => {
    if (warningsCount > 0 && errorMessages !== undefined) {
      toast.warning(
        <div className="flex flex-col gap-2">
          <span className="font-semibold">{contactName ?? t('contacts.toast.contact')}</span>
          <div className="text-xs opacity-80">{t('contacts.toast.warningsFound')}</div>
          <ul className="space-y-1 text-sm">
            {errorMessages.map((msg, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="opacity-60 mt-0.5">•</span>
                <span>{translateErrorMessage(msg, t)}</span>
              </li>
            ))}
          </ul>
        </div>,
        {
          duration: 6000,
          style: {
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            color: '#92400e',
          },
        },
      );
    }
  };

  if (warningsCount > 0) {
    return (
      <button
        type="button"
        onClick={handleShowWarnings}
        className="cursor-pointer"
        title={t('contacts.toast.clickToSee')}
      >
        <AlertTriangle className="h-4 w-4 text-amber-500 hover:text-amber-600" />
      </button>
    );
  }
  if (warningsCount === 0) {
    return <Check className="h-4 w-4 text-emerald-600" />;
  }
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

export default function ContactsTable ({
  isLoading,
  contacts,
  skeletonRowsCount = 8,
  onEditContact,
  onDeleteContact,
  activeTab
}: ContactsTableProps): React.JSX.Element {
  const { t } = useTranslation();
  const contactsList = Array.isArray(contacts) ? contacts : [];

  return (
    <div className="w-full rounded-md border border-border bg-card">
      {activeTab === 'salesforce' && isLoading ? (
        <div>
          <ContactsFallbackComponent
            message={t('contacts.table.folderFallback')}
            verticalPadding={30}
          >
            <IconFolder stroke={0.5} className="w-10 h-10 text-gray-500"/>
          </ContactsFallbackComponent>
          <div className='flex justify-center bg-background text-gray-500 text-md pb-3'>
            {contacts?.length === 0 && t('contacts.table.notSelected')}
          </div>
        </div>
      ) : (
        <Table className="w-full table-fixed">
          <TableHeader className="bg-muted/60">
            <TableRow className="border-b border-border/60">
              <TableHead className="w-[60px]">{t('contacts.table.status')}</TableHead>
              <TableHead className="w-[18%]">{t('contacts.table.name')}</TableHead>
              <TableHead className="w-[18%]">{t('contacts.table.company')}</TableHead>
              <TableHead className="w-[14%]">{t('contacts.table.position')}</TableHead>
              <TableHead className="w-[14%]">{t('contacts.table.phone')}</TableHead>
              <TableHead className={`${activeTab === 'csv' ? 'w-auto' : 'w-[20%]'}`}>{t('contacts.table.notes')}</TableHead>
              {activeTab === 'csv' &&
                <TableHead className="w-[90px] text-right">{t('contacts.table.actions')}</TableHead>
              }
            </TableRow>
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <ContactsTableSkeleton rows={skeletonRowsCount} />
            ) : contactsList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={activeTab === 'csv' ? 7 : 6} className="py-12 text-center text-sm text-muted-foreground">
                  <ContactsFallbackComponent
                    message={activeTab !== 'salesforce' ? 'contacts.table.empty' : 'contacts.table.folderFallback'}
                    verticalPadding={30}>
                    {activeTab === 'salesforce' && <IconFolder stroke={0.5} className="w-10 h-10 text-gray-500"/>}
                  </ContactsFallbackComponent>
                </TableCell>
              </TableRow>
            ) : (
              contactsList.map((contact, r) => (
                <TableRow data-qa-id={`contact-row-${r}`} className="border-b border-border hover:bg-muted/30" key={contact.id}>
                  <TableCell>
                    <ContactStatusIcon
                      errorMessages={contact.errorMessages}
                      contactName={contact.name}
                      t={t}
                    />
                  </TableCell>
                  <TableCell data-qa-id={`contact-name-${r}`} className="truncate font-medium" title={formatDisplayValue(contact.name)}>
                    {formatDisplayValue(contact.name)}
                  </TableCell>
                  <TableCell data-qa-id={`contact-company-${r}`} className="truncate text-muted-foreground" title={formatDisplayValue(contact.accountName)}>
                    {formatDisplayValue(contact.accountName)}
                  </TableCell>
                  <TableCell data-qa-id={`contact-position-${r}`} className="truncate text-muted-foreground" title={formatDisplayValue(contact.jobTitle)}>
                    {formatDisplayValue(contact.jobTitle)}
                  </TableCell>
                  <TableCell data-qa-id={`contact-phone-${r}`} className="truncate text-muted-foreground" title={formatDisplayValue(contact.phone)}>
                    {formatDisplayValue(contact.phone)}
                  </TableCell>
                  <TableCell data-qa-id={`contact-notes-${r}`} className="truncate text-muted-foreground" title={getContactNotes(contact)}>
                    {getContactNotes(contact)}
                  </TableCell>

                  {activeTab === 'csv' &&
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {onEditContact !== undefined && (
                          <button
                            type="button"
                            className="cursor-pointer"
                            title={t('contacts.table.edit')}
                            onClick={() => {
                              onEditContact(contact);
                            }}
                          >
                            <Pencil className="w-4 h-4 text-sky-500 hover:text-sky-800" />
                          </button>
                        )}

                        {onDeleteContact !== undefined && (
                          <button
                            type="button"
                            className="cursor-pointer"
                            title={t('contacts.table.delete')}
                            onClick={() => {
                              onDeleteContact(contact.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-600 hover:text-red-800" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  }
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}